<template>
  <div class="hero-shell w-full mx-auto overflow-hidden border border-neutral-300/70 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.55)] bg-brand-900">
    <div class="hero-carousel relative">
      <template v-for="({ slide, index }) in activeSlideEntries" :key="slide.id">
        <article :class="['relative hero-article w-full', { 'hero-article--fairmont': isFairmontSlide(slide.id) }]">
          <picture class="absolute inset-0 block h-full w-full">
            <source
              v-if="slide.mobileSrcset"
              media="(max-width: 767px)"
              :srcset="slide.mobileSrcset"
              sizes="100vw"
            />
            <img
              :src="slide.image"
              :srcset="slide.srcset"
              :sizes="slide.sizes"
              :width="slide.width || 1920"
              :height="slide.height"
              :alt="slide.title"
              :class="['h-full w-full object-cover', getSlideImageClass(slide.id)]"
              :fetchpriority="index === 0 ? 'high' : 'low'"
              :loading="index === 0 ? 'eager' : 'lazy'"
              decoding="async"
            />
          </picture>
          <div :class="['hero-overlay absolute inset-0 bg-gradient-to-r from-black via-black/60 to-black/20', { 'hero-overlay--fairmont': isFairmontSlide(slide.id) }]"></div>
          <div class="absolute inset-0 hero-pattern"></div>

          <div v-if="slide.id === 'fairmont' || slide.id === 'fairmont-en'" class="fairmont-spotlight hidden md:block" aria-hidden="true">
            <span class="fairmont-spotlight-dot"></span>
            <span class="fairmont-spotlight-ring"></span>
            <span class="fairmont-spotlight-line"></span> 
            <span class="fairmont-spotlight-tag">Fairmont Clock Tower</span>
          </div>

          <div :class="['relative z-10 container h-full hero-inner flex flex-col justify-between py-6 md:py-10', { 'hero-inner--fairmont': isFairmontSlide(slide.id) }]">
            <div class="hero-meta flex justify-between items-start gap-2">
              <span class="hero-license inline-flex items-center rounded-full border border-white/25 bg-black/20 px-3 py-1 text-[11px] md:text-xs text-slate-100 backdrop-blur-sm">
                <font-awesome-icon icon="fa-solid fa-shield-halved" class="mr-1.5 h-3 w-3 text-accent-300" />
                {{ locale === 'en' ? 'Inanctur Doc No:7677' : 'İnançtur Turizm Belge:7677' }}
              </span>

              <span class="hidden md:inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm border border-white/20">
                <font-awesome-icon icon="fa-solid fa-hand-sparkles" class="h-3 w-3 text-accent-300"></font-awesome-icon>
                {{ slide.topPill }}
              </span>
            </div>

            <div :class="['hero-content-grid grid gap-5 md:gap-10 items-end pb-2 md:pb-6', { 'hero-content-grid--fairmont': isFairmontSlide(slide.id), 'md:grid-cols-[1fr_auto]': !isFairmontSlide(slide.id) }]">
              <div v-if="slide.id === 'fairmont' || slide.id === 'fairmont-en'" :class="['max-w-xl', isFairmontSlide(slide.id) ? 'hero-left-premium hero-copy-premium' : '']">
               <span :class="['absolute -top-10 right-5 md:-top-10 md:left-0 md:right-auto md:inline-flex items-center rounded-full bg-accent-600/90 text-white px-3 py-1 text-[11px] md:text-xs font-semibold shadow-lg mb-2 md:mb-5', isFairmontSlide(slide.id) ? 'hero-badge-premium' : '']">
                  {{ slide.badge }}
                </span> 
                <h3 v-if="slide.title2" :class="['text-white text-left text-xl md:text-3xl leading-tight font-1 drop-shadow-[0_8px_16px_rgba(0,0,0,0.42)] mb-0 pb-0', isFairmontSlide(slide.id) ? 'hero-title2-premium' : '']">
                  {{ slide.title2 }}
                </h3>
                <h2 :class="['text-white text-left text-2xl md:text-4xl leading-tight font-1 drop-shadow-[0_8px_16px_rgba(0,0,0,0.42)] mt-2 mb:0 md:mb-4 pb-0', isFairmontSlide(slide.id) ? 'hero-title-main-premium' : '', slide.id === 'fairmont' ? 'hero-title-script' : '']">
                  {{ slide.title }}
                </h2>
              
                <div v-if="slide.title3" :class="['text-white h-10 text-left leading-tight font-1 drop-shadow-[0_8px_16px_rgba(0,0,0,0.42)] mb-0 pb-0 relative overflow-hidden', isFairmontSlide(slide.id) ? 'hero-title3-premium' : '']">
                  <template v-if="Array.isArray(slide.title3)">
                    <Transition name="title3-rotate">
                      <span v-if="titleRotateVisible" :key="titleRotateIndex" class="flex items-center gap-2.5 truncate">
                        <img v-if="slide.title3[titleRotateIndex].img" :src="slide.title3[titleRotateIndex].img" :alt="slide.title3[titleRotateIndex].text" class="w-7 h-7 rounded-full object-contain" />
                        <span v-else  class="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full title3-icon-circle" :style="{ background: slide.title3[titleRotateIndex].bg }">
                          <font-awesome-icon :icon="slide.title3[titleRotateIndex].icon" class="w-3.5 h-3.5 text-white" />
                        </span>
                        <span class="truncate text-lg md:text-xl font-semibold">{{ slide.title3[titleRotateIndex].text }}</span>
                      </span>
                    </Transition>
                  </template>
                  <template v-else>{{ slide.title3 }}</template>
                </div>
                <p :class="['mt-2 md:mt-3 text-left text-slate-100 text-sm md:text-lg font-1 max-w-lg', isFairmontSlide(slide.id) ? 'hero-desc-premium' : '']">
                  {{ slide.description }}
                </p>

                <div :class="['mt-4 md:mt-6 flex items-center gap-2 md:gap-3', isFairmontSlide(slide.id) ? 'hero-cta-wrap-premium' : '']">
                  <button type="button" class="hero-cta-primary" @click="goPath(slide.primaryCta.path)">
                    <span>{{ slide.primaryCta.label }}</span>
                    <font-awesome-icon icon="fa-solid fa-arrow-right" class="h-3 w-3" />
                  </button>
                  <button type="button" class="hero-cta-secondary" @click="goPath(slide.secondaryCta.path)">
                    <font-awesome-icon icon="fa-solid fa-circle-check" class="h-3 w-3 text-accent-300" />
                    <span>{{ slide.secondaryCta.label }}</span>
                  </button>
                </div>
              </div><div v-else :class="['max-w-xl', isFairmontSlide(slide.id) ? 'hero-left-premium' : '']">
               <span :class="['absolute top-2 right-5 md:-top-6 md:left-0 md:right-auto md:inline-flex items-center rounded-full bg-accent-500/90 text-white px-3 py-1 text-[11px] md:text-xs font-semibold shadow-lg mb-2 md:mb-4', isFairmontSlide(slide.id) ? 'hero-badge-premium' : '']">
                  {{ slide.badge }}
                </span>


                <h3 v-if="slide.title2" :class="['text-white text-left text-xl md:text-3xl leading-tight font-display drop-shadow-[0_8px_16px_rgba(0,0,0,0.42)] mb-0 pb-0', isFairmontSlide(slide.id) ? 'hero-title2-premium' : '']">
                  {{ slide.title2 }}
                </h3>
                <h2 :class="['text-white text-left text-3xl md:text-5xl leading-tight font-display drop-shadow-[0_8px_16px_rgba(0,0,0,0.42)] mb-0 pb-0 my-0', isFairmontSlide(slide.id) ? 'hero-title-main-premium' : '']">
                  {{ slide.title }}
                </h2>
              
                <h3 v-if="slide.title3" :class="['text-white text-left text-3xl md:text-4xl leading-tight font-display drop-shadow-[0_8px_16px_rgba(0,0,0,0.42)] mb-0 pb-0', isFairmontSlide(slide.id) ? 'hero-title3-premium' : '']">
                  {{ slide.title3 }}
                </h3>
                <p :class="['mt-2 md:mt-3 text-left text-slate-100 text-sm md:text-lg font-1 max-w-lg', isFairmontSlide(slide.id) ? 'hero-desc-premium' : '']">
                  {{ slide.description }}
                </p>

                <div :class="['mt-4 md:mt-6 flex items-center gap-2 md:gap-3', isFairmontSlide(slide.id) ? 'hero-cta-wrap-premium' : '']">
                  <button type="button" class="hero-cta-primary" @click="goPath(slide.primaryCta.path)">
                    {{ slide.primaryCta.label }}
                  </button>
                  <button type="button" class="hero-cta-secondary" @click="goPath(slide.secondaryCta.path)">
                    {{ slide.secondaryCta.label }}
                  </button>
                </div>
              </div>


              <!-- Fairmont Premium Panel -->
              <div v-if="slide.id === 'fairmont' || slide.id === 'fairmont-en'" class="fairmont-side-premium hidden md:flex flex-col gap-3 min-w-[330px] max-w-[380px]">
                <div class="flex items-center gap-2 mb-1">
                  <span class="h-px flex-1 bg-gradient-to-r from-accent-400/60 to-transparent"></span>
                  <span class="fairmont-side-title text-[10px] font-bold tracking-widest uppercase text-accent-300">{{ locale === 'en' ? 'Programme at a glance' : 'Program özeti' }}</span>
                  <span class="h-px flex-1 bg-gradient-to-l from-accent-400/60 to-transparent"></span>
                </div>

                <div class="fairmont-hero-card fairmont-hero-card-premium group relative overflow-hidden rounded-2xl border border-accent-400/50 p-4 ring-1 ring-accent-500/20">
                  <div class="fairmont-glow"></div>
                  <div class="relative z-10">
                    <div class="block">
                      <div class="flex items-center gap-2.5">
                        <div class="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-accent-500/20 ring-1 ring-accent-400/40">
                          <img src="/assets/img/hotels/fairmont-2.webp" alt="Fairmont Hotels" class="h-14 w-14" />
                        </div>
                        <div>
                          <div class="text-[10px] font-semibold uppercase tracking-wider text-left text-accent-400">{{ locale === 'en' ? 'Makkah accommodation' : 'Mekke konaklaması' }}</div>
                          <div class="text-sm font-bold text-left text-white leading-tight">Fairmont Clock Tower</div>
                          <div class=" flex items-center gap-1.5">
                            <div class="flex gap-0.5">
                              <font-awesome-icon v-for="s in 5" :key="s" icon="fa-solid fa-star" class="h-2.5 w-2.5 text-accent-400"></font-awesome-icon>
                            </div>
                            <span class="text-[10px] text-neutral-300">{{ locale === 'en' ? 'One named hotel for the group' : 'Grup için ismi açıklanan tek otel' }}</span>
                          </div>
                        </div>
                           <span class="fairmont-pulse-badge flex-shrink-0 rounded-full bg-accent-600/90 px-2 py-0.5 text-[9px] font-bold text-white">
                        {{ locale === 'en' ? 'Named hotel' : 'İsmi açık otel' }}
                      </span>
                      </div>
                   
                    </div>
                    
                    <!-- <div class="mt-2.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[10px] text-neutral-200 leading-relaxed">
                      {{ locale === 'en' ? '🕌 Clock Tower view suites · Private Haram entry · Curated premium hospitality' : '🕌 Saat Kulesi manzaralı suit odalar · Harem özel girişi · Seçkin premium hizmet' }}
                    </div> -->
                  </div>
                </div>

                <div class="fairmont-info-card fairmont-train-card relative overflow-hidden rounded-2xl border border-white/20 backdrop-blur-md px-3 py-2 shadow-md">
                  <div class="train-track-line"></div>
                  <div class="relative z-10 flex items-center gap-3">
                    <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-accent-500/20 ring-1 ring-accent-400/35">
                      <font-awesome-icon icon="fa-solid fa-train-subway" class="h-4 w-4 text-accent-300"></font-awesome-icon>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="text-[10px] font-semibold uppercase tracking-wider text-left text-accent-300">{{ locale === 'en' ? 'High-speed rail' : 'Hızlı tren geçişi' }}</div>
                      <div class="flex items-center gap-1.5 mt-0.5">
                        <span class="text-xs font-bold text-white">{{ locale === 'en' ? 'Medina' : 'Medine' }}</span>
                        <span class="train-arrow text-accent-300">⟶</span>
                        <span class="text-xs font-bold text-white">{{ locale === 'en' ? 'Mecca' : 'Mekke' }}</span>
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-[9px] text-accent-200/70">{{ locale === 'en' ? 'Approx.' : 'Yaklaşık' }}</div>
                      <div class="text-sm font-extrabold text-accent-200">3s</div>
                    </div>
                  </div>
                </div>

                <div class="fairmont-info-card fairmont-guide-card relative overflow-hidden rounded-2xl border border-white/20 backdrop-blur-md px-3 py-2 shadow-md">
                  <div class="relative z-10 flex items-center gap-3">
                    <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-accent-500/20 ring-1 ring-accent-400/35">
                      <font-awesome-icon icon="fa-solid fa-user-tie" class="h-4 w-4 text-accent-300"></font-awesome-icon>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="text-[10px] font-semibold uppercase tracking-wider text-left text-accent-300">{{ locale === 'en' ? 'Guidance and field support' : 'Rehberlik ve saha desteği' }}</div>
                      <div class="text-xs font-semibold text-white mt-0.5">{{ locale === 'en' ? 'The tour scholar focuses on guests while the field team manages operations.' : 'Tur hocası misafire odaklanırken saha ekibi operasyonu takip eder.' }}</div>
                    </div>
                    <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-accent-400/30 bg-accent-500/10">
                      <font-awesome-icon icon="fa-solid fa-headset" class="h-4 w-4 text-accent-200" />
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="slide.id !== 'umre' && slide.id !== 'umrah' && slide.id !== 'fairmont' && slide.id !== 'fairmont-en'" class="hidden md:flex flex-col gap-2 min-w-[300px]">
                <div
                  v-for="point in slide.points"
                  :key="point"
                  class="inline-flex items-center gap-2 rounded-xl bg-white/85 backdrop-blur text-slate-800 shadow px-3 py-2 text-sm font-medium"
                >
                  <font-awesome-icon icon="fa-solid fa-check-circle" class="w-4 h-4 text-support-600"></font-awesome-icon>
                  {{ point }}
                </div>
              </div>

              <!-- En Yakın Umre Tour Kartı -->
              <div v-if="(slide.id === 'umre' || slide.id === 'umrah') && nearestTour && !loading" class="hidden md:flex flex-col gap-3 min-w-[360px] rounded-2xl border border-white/25 bg-brand-900/55 p-4 shadow-[0_18px_30px_-20px_rgba(22,32,51,0.55)]">
                <div class="text-[11px] font-semibold text-neutral-200 uppercase tracking-[0.08em]">{{ locale === 'en' ? 'Nearest Departure' : 'En Yakın Tarih' }}</div>

                <!-- Tarih Badge -->
                <div class="rounded-lg border border-white/20 bg-white/5 px-3 py-3 flex items-center gap-3">
                  <div class="rounded-lg border border-white/25 bg-neutral-900  px-2.5 py-1.5 text-center min-w-[56px]">
                    <div class="text-lg leading-4 font-bold text-accent-200">{{ getDateDay(nearestTour.turs_StartDate) }}</div>
                    <div class="text-[8px] font-semibold text-accent-200">{{ getDateMonth(nearestTour.turs_StartDate) }}</div>
                  </div>
                  <div class="flex-1">
                    <div class="text-sm font-semibold text-white truncate">{{ nearestTour.bestPrice.sure }} Gün Umre</div>
                    <div class="text-xs text-neutral-300 truncate">{{ nearestTour.start_mini }}</div>
                  </div>
                </div>

                <!-- Otel Bilgisi -->
                <div class="space-y-2">
                  <div class="flex items-center gap-2.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-2">
                    <img v-if="nearestTour.otel1_photo" :src="showImage(nearestTour.otel1_photo, 80, 80)" :alt="nearestTour.otel1" class="h-10 w-10 rounded-full ring-1 ring-white/25" />
                    <div class="min-w-0 flex-1">
                      <div class="text-xs font-medium text-white truncate">{{ nearestTour.otel1 }}</div>
                      <div class="text-[10px] text-neutral-300">{{ nearestTour.otel1_city }}</div>
                    </div>
                  </div>
                  <div v-if="nearestTour.otel2" class="flex items-center gap-2.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-2">
                    <img v-if="nearestTour.otel2_photo" :src="showImage(nearestTour.otel2_photo, 80, 80)" :alt="nearestTour.otel2" class="h-10 w-10 rounded-full ring-1 ring-white/25" />
                    <div class="min-w-0 flex-1">
                      <div class="text-xs font-medium text-white truncate">{{ nearestTour.otel2 }}</div>
                      <div class="text-[10px] text-neutral-300">{{ nearestTour.otel2_city }}</div>
                    </div>
                  </div>
                </div>

                <!-- Fiyat ve CTA -->
                <div v-if="nearestTour.bestPrice" class="space-y-2.5 pt-2 border-t border-white/15">
                  <div class="flex items-center justify-between text-xs text-neutral-300 gap-2">
                    <span class="truncate">{{ nearestTour.bestPrice.sure }} {{ locale === 'en' ? 'days' : 'gün' }}</span>
                    <span class="truncate">{{ locale === 'en' ? 'Return' : 'Dönüş' }}: {{ nearestTour.bestPrice.end_mini }}</span>
                  </div>
                  <div class="flex items-baseline gap-2">
                    <div v-if="nearestTour.bestPrice.price_high && nearestTour.bestPrice.price < nearestTour.bestPrice.price_high" class="text-xs text-neutral-400 line-through">
                      {{ formatMoney(nearestTour.bestPrice.price_high) }}
                    </div>
                    <div class="text-xl font-semibold text-white">
                      {{ formatMoney(nearestTour.bestPrice.price) }}<span class="text-sm text-neutral-300 ml-1">{{ getCurrencySymbol(nearestTour.bestPrice.currency) }}</span>
                    </div>
                  </div>
                  <button @click="goPath(`/tourDetail/${nearestTour.bestPrice.id}`)" class="w-full mt-3 inline-flex items-center justify-center rounded-lg border border-white/25 bg-primary-500/70 hover:bg-white/15 text-white px-3 py-2.5 text-xs font-semibold transition duration-200">
                    {{ locale === 'en' ? 'View Details' : 'Detayları Gör' }} →
                  </button>
                </div>
              </div>

              <div v-if="(slide.id === 'umre' || slide.id === 'umrah') && loading" class="hidden md:flex flex-col gap-3 min-w-[360px] bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4 items-center justify-center h-64">
                <div class="animate-spin inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-white/20 border-t-accent-400"></div>
                <div class="text-xs text-white/50">{{ locale === 'en' ? 'Loading' : 'Yükleniyor' }}...</div>
              </div>
            </div>
          </div>
        </article>
      </template>

      <button
        type="button"
        class="hero-navigation hero-navigation-prev"
        :aria-label="locale === 'en' ? 'Previous slide' : 'Önceki slayt'"
        @click="showPreviousSlide"
      >
        <font-awesome-icon icon="fa-solid fa-chevron-left" class="w-4 h-4" />
      </button>
      <button
        type="button"
        class="hero-navigation hero-navigation-next"
        :aria-label="locale === 'en' ? 'Next slide' : 'Sonraki slayt'"
        @click="showNextSlide"
      >
        <font-awesome-icon icon="fa-solid fa-chevron-right" class="w-4 h-4" />
      </button>

      <div class="hero-pagination" role="group" :aria-label="locale === 'en' ? 'Hero slides' : 'Tanıtım slaytları'">
        <button
          v-for="(slide, index) in localizedSlides"
          :key="`pagination-${slide.id}`"
          type="button"
          :class="['hero-pagination-dot', { 'is-active': index === activeSlide }]"
          :aria-label="`${locale === 'en' ? 'Show slide' : 'Slaytı göster'} ${index + 1}`"
          :aria-current="index === activeSlide ? 'true' : undefined"
          @click="activeSlide = index"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const { locale } = useI18n()
const { goPath, showImage, formatMoney } = useCommon()
const config = useRuntimeConfig()

// The scripted Fairmont title is above the fold. Discover its font with the
// document instead of waiting for the stylesheet to be parsed first.
useHead({
  link: [
    {
      rel: 'preload',
      as: 'font',
      type: 'font/woff2',
      href: '/assets/fonts/allura-latin.woff2',
      crossorigin: 'anonymous',
    },
  ],
})

const activeSlide = ref(0)
const nearestTour = ref(null)
const loading = ref(false)

const parseTourDate = (dateValue) => {
  const [year, month, day] = String(dateValue || '').slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return null
  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? null : date
}

const formatTourDate = (dateValue, options) => {
  const date = parseTourDate(dateValue)
  if (!date) return ''
  return new Intl.DateTimeFormat(locale.value === 'tr' ? 'tr-TR' : 'en-US', options).format(date)
}

const getDateDay = (dateValue) => {
  const date = parseTourDate(dateValue)
  return date ? String(date.getDate()).padStart(2, '0') : ''
}

const getDateMonth = (dateValue) => {
  return formatTourDate(dateValue, { month: 'short' }).toUpperCase()
}

const getDateWeekday = (dateValue) => {
  return formatTourDate(dateValue, { weekday: 'long' })
}

const getCurrencySymbol = (currency) => {
  if (currency === 'TL') return '₺'
  if (currency === 'EURO') return '€'
  return '$'
}

const isFairmontSlide = (slideId) => ['fairmont', 'fairmont-en'].includes(slideId)

// ── Title3 array rotation ───────────────────────────────
const titleRotateIndex = ref(0)
const titleRotateVisible = ref(true)
let titleRotateTimer = null

const stopTitleRotation = () => {
  if (titleRotateTimer) {
    clearTimeout(titleRotateTimer)
    titleRotateTimer = null
  }
}

const runTitleRotation = (strings) => {
  stopTitleRotation()
  titleRotateIndex.value = 0
  titleRotateVisible.value = true
  const cycle = () => {
    titleRotateTimer = setTimeout(() => {
      titleRotateVisible.value = false
      titleRotateTimer = setTimeout(() => {
        titleRotateIndex.value = (titleRotateIndex.value + 1) % strings.length
        titleRotateVisible.value = true
        cycle()
      }, 480)
    }, 2800)
  }
  cycle()
}

onMounted(() => {
  const firstSlide = localizedSlides.value[0]
  if (isFairmontSlide(firstSlide?.id) && Array.isArray(firstSlide?.title3)) {
    runTitleRotation(firstSlide.title3)
  }
})

onBeforeUnmount(() => stopTitleRotation())

const fetchNearestTour = async () => {
  loading.value = true
  nearestTour.value = null
  
  const tday = new Date().toISOString().slice(0, 10)

  try {
    const response = await $fetch(`${config.public.apiBase}/graphql/direct`, {
      method: 'post',
      body: {
        query: `{
          tours(startLeast: "${tday}", pdb_status:1, category:"5YILDIZUMRE", turs_status:"ALLOPEN", sureLeast: "4", sureMost: "21", limit: 1, order: "turs_EndDate ASC") {
            pdb_Id
            turs_StartDate
            turs_EndDate
            tur_Sure
            tour_Currency
            turs_double
            turs_double_high
            otel1_photo
            otel2_photo
            otel1 {
              sOtelAdi { tr en }
              city { tr en }
            }
            otel2 {
              sOtelAdi { tr en }
              city { tr en }
            }
          }
        }`
      }
    })

    const tour = response?.data?.tours?.[0]
    if (tour) {
      const bestPrice = {
        id: tour.pdb_Id,
        sure: tour.tur_Sure,
        currency: tour.tour_Currency,
        price: Number(tour.turs_double || 0),
        price_high: Number(tour.turs_double_high || 0),
        end_mini: formatTourDate(tour.turs_EndDate, { day: '2-digit', month: 'short' })
      }

      nearestTour.value = {
        turs_StartDate: tour.turs_StartDate,
        start_mini: formatTourDate(tour.turs_StartDate, { day: '2-digit', month: 'short', weekday: 'short' }),
        otel1_photo: tour.otel1_photo,
        otel1: tour.otel1?.sOtelAdi?.[locale.value] || tour.otel1?.sOtelAdi?.tr,
        otel1_city: tour.otel1?.city?.[locale.value] || tour.otel1?.city?.tr,
        otel2_photo: tour.otel2_photo,
        otel2: tour.otel2?.sOtelAdi?.[locale.value] || tour.otel2?.sOtelAdi?.tr,
        otel2_city: tour.otel2?.city?.[locale.value] || tour.otel2?.city?.tr,
        bestPrice
      }
    }
  } catch (error) {
    console.error('Error fetching nearest tour:', error)
  } finally {
    loading.value = false
  }
}

const trSlides = [
  {
    id: 'fairmont',
    image: '/assets/img/slider/banner_1-1280.webp',
    width: 2046,
    height: 1234,
    srcset: [
      '/assets/img/slider/banner_1-640.webp 640w',
      '/assets/img/slider/banner_1-960.webp 960w',
      '/assets/img/slider/banner_1-1280.webp 1280w',
      '/assets/img/slider/banner_1-1600.webp 1600w',
      '/assets/img/slider/banner_1.webp 2046w',
    ].join(', '),
    mobileImage: '/assets/img/slider/banner_1-960-q42.webp',
    mobileSrcset: [
      '/assets/img/slider/banner_1-640.webp 640w',
      '/assets/img/slider/banner_1-960-q42.webp 960w',
    ].join(', '),
    sizes: '(max-width: 767px) 100vw, (max-width: 1380px) 100vw, 1380px',
    topPill: 'Premium umre standardı',
    badge: 'Kesin kalkışlı umre',
    title: "Özel Umre",
    title2: "Fairmont Clock Tower",
    title3: 'Türk Hava Yolları · Hızlı tren · Rehberlik',
    description: 'Mekke’de Fairmont konaklaması, Medine–Mekke hızlı tren geçişi ve yolculuk boyunca rehberlik desteği.',
    primaryCta: { label: 'Programları karşılaştır', path: '/tour/umre-turlari' },
    secondaryCta: { label: 'Hizmet standardı', path: '/page/umre-hizmet-standartlari' },
    points: []
  },
  {
    id: 'umre',
    image: '/assets/img/slider/kabe-slider-1920.webp',
    topPill: 'Yeni Sezon',
    badge: '2026 Umre Turları',
    title: 'Umre Turlarında',
    title2: '5 Yıldızlı Hizmet',
    title3: 'Doğru Tercih',
    description: 'Net program, merkezi konaklama ve deneyimli rehberlik.',
    primaryCta: { label: 'Umre Turlarını İncele', path: '/tour/umre-turlari' },
    secondaryCta: { label: 'Fiyatları Karşılaştır', path: '/tour/umre-turlari' },
    points: ['5 Yıldızlı Konaklama', 'Deneyimli Rehber Kadrosu', '%97 Yolcu Memnuniyeti']
  },
  {
    id: 'yurtdisi',
    image: '/assets/img/slider/Mostar_2560.webp',
    topPill: 'Populer Rotalar',
    badge: '2026 Yurtdışı Turları',
    title: 'Balkanlardan Endülüs’e İlham Veren Rotalar',
    description: 'Kültür ve tarihi, özenle planlanmış rotalarda keşfedin.',
    primaryCta: { label: 'Yurtdışı Turlar', path: '/tours/rota-yurtdisi-turlari' },
    secondaryCta: { label: 'Balkan Turları', path: '/tours/rota-balkan-turlari' },
    points: ['Uzman Operasyon Ekibi', 'Konforlu Ulaşım ve Oteller', 'Planlı ve Şeffaf Program']
  },
  {
    id: 'ozel',
    image: '/assets/img/slider/home-slider-light.webp',
    topPill: 'Kişiye Özel',
    badge: 'Esnek Programlar',
    title: 'Size Özel Umre Planı, Size Özel Konfor',
    description: 'Tarih, süre ve otel seçimini size göre planlayın.',
    primaryCta: { label: 'Özel Umre Oluştur', path: '/tour/kisiye-ozel-umre-turlari' },
    secondaryCta: { label: 'Müşteri Yorumları', path: '/page/yorumlar' },
    points: ['Esnek Tarih ve Süre', 'Aileye Uygun Alternatifler', 'Hızlı Rezervasyon Desteği']
  }
]

const enSlides = [
  {
    id: 'fairmont-en',
     image: '/assets/img/slider/banner_1-1280.webp',
    width: 2046,
    height: 1234,
    srcset: [
      '/assets/img/slider/banner_1-640.webp 640w',
      '/assets/img/slider/banner_1-960.webp 960w',
      '/assets/img/slider/banner_1-1280.webp 1280w',
      '/assets/img/slider/banner_1-1600.webp 1600w',
      '/assets/img/slider/banner_1.webp 2046w',
    ].join(', '),
    mobileImage: '/assets/img/slider/banner_1-960-q42.webp',
    mobileSrcset: [
      '/assets/img/slider/banner_1-640.webp 640w',
      '/assets/img/slider/banner_1-960-q42.webp 960w',
    ].join(', '),
    sizes: '(max-width: 767px) 100vw, (max-width: 1380px) 100vw, 1380px',
    topPill: 'Premium Umrah standard',
    badge: 'Scheduled Umrah departure',
    title: 'A Private Umrah',
    title2: 'Fairmont Clock Tower',
    title3: 'Turkish Airlines · High-speed rail · Guidance',
    description: 'Fairmont accommodation in Makkah, high-speed rail between Madinah and Makkah, and guidance throughout the journey.',
    primaryCta: { label: 'Compare programmes', path: '/tour/umre-turlari' },
    secondaryCta: { label: 'Service standard', path: '/page/umre-hizmet-standartlari' },
    points: []
  },
  {
    id: 'umrah',
    image: '/assets/img/slider/kabe-slider-1920.webp',
    topPill: 'New Season',
    badge: '2026 Umrah Packages',
    title: 'Plan Your Spiritual Journey with Comfort',
    description: 'Clear programmes, central hotels and expert guidance.',
    primaryCta: { label: 'Explore Umrah Tours', path: '/tour/umre-turlari' },
    secondaryCta: { label: 'Compare Prices', path: '/tour/umre-turlari' },
    points: ['5-Star Accommodation', 'Expert Tour Guides', '97% Guest Satisfaction']
  },
  {
    id: 'international',
    image: '/assets/img/slider/Mostar_2560.webp',
    topPill: 'Popular Routes',
    badge: '2026 International Tours',
    title: 'Inspiring Routes from Balkans to Andalusia',
    description: 'Discover culture and history on carefully planned routes.',
    primaryCta: { label: 'International Tours', path: '/tours/rota-yurtdisi-turlari' },
    secondaryCta: { label: 'Balkan Tours', path: '/tours/rota-balkan-turlari' },
    points: ['Experienced Operations Team', 'Comfort-First Planning', 'Transparent Itineraries']
  },
  {
    id: 'custom',
    image: '/assets/img/slider/home-slider-light.webp',
    topPill: 'Tailor Made',
    badge: 'Flexible Programs',
    title: 'Build a Personalized Umrah Program',
    description: 'Set your dates, duration and hotel preference.',
    primaryCta: { label: 'Create Private Umrah', path: '/tour/kisiye-ozel-umre-turlari' },
    secondaryCta: { label: 'Read Reviews', path: '/page/yorumlar' },
    points: ['Flexible Date Options', 'Family-Friendly Alternatives', 'Fast Reservation Support']
  }
]

const localizedSlides = computed(() => (locale.value === 'en' ? enSlides : trSlides))

const lcpSlide = computed(() => localizedSlides.value[0])

useHead(() => ({
  link: lcpSlide.value?.image
    ? [
        {
          key: 'home-hero-lcp-image-mobile',
          rel: 'preload',
          as: 'image',
          type: 'image/webp',
          href: lcpSlide.value.mobileImage,
          imagesrcset: lcpSlide.value.mobileSrcset,
          imagesizes: '100vw',
          media: '(max-width: 767px)',
          fetchpriority: 'high',
          tagPriority: 'critical',
        },
        {
          key: 'home-hero-lcp-image-desktop',
          rel: 'preload',
          as: 'image',
          type: 'image/webp',
          href: lcpSlide.value.image,
          imagesrcset: lcpSlide.value.srcset,
          imagesizes: lcpSlide.value.sizes,
          media: '(min-width: 768px)',
          fetchpriority: 'high',
          tagPriority: 'critical',
        },
      ]
    : [],
}))

const activeSlideEntries = computed(() => {
  const slide = localizedSlides.value[activeSlide.value]
  return slide ? [{ slide, index: activeSlide.value }] : []
})

const showPreviousSlide = () => {
  activeSlide.value = (activeSlide.value - 1 + localizedSlides.value.length) % localizedSlides.value.length
}

const showNextSlide = () => {
  activeSlide.value = (activeSlide.value + 1) % localizedSlides.value.length
}

const getSlideImageClass = (slideId) => {
  if (['umre', 'umrah'].includes(slideId)) return 'hero-image-kabe'
  if (['fairmont', 'fairmont-en'].includes(slideId)) return 'hero-image-fairmont'
  return ''
}

watch(activeSlide, (newSlide) => {
  const currentSlide = localizedSlides.value[newSlide]
  if (isFairmontSlide(currentSlide?.id) && Array.isArray(currentSlide?.title3)) {
    runTitleRotation(currentSlide.title3)
  } else {
    stopTitleRotation()
  }

  // The departure card is not visible on the initial slide. Fetch its data
  // only when the visitor asks to see an Umrah slide.
  if ((currentSlide?.id === 'umre' || currentSlide?.id === 'umrah') && !nearestTour.value && !loading.value) {
    void fetchNearestTour()
  }
  
})

watch(locale, () => {
  activeSlide.value = 0
})
 
</script>

<style scoped>
/* ─── Fairmont Premium Card ─────────────────────────────── */
.fairmont-hero-card {
  animation: fairmont-entrance 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.hero-title-script {
  font-family: 'Allura', cursive;
  font-size: clamp(2.2rem, 4.5vw, 4.2rem);
  font-weight: 400;
  line-height: 0.98;
  letter-spacing: 0.02em;
  text-shadow: 0 14px 30px rgba(0, 0, 0, 0.52), 0 0 18px rgba(230, 185, 74, 0.28);
}

@keyframes fairmont-entrance {
  from { opacity: 0; transform: translateY(18px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)   scale(1); }
}

.fairmont-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 30% 40%, rgba(180,122,22,0.18) 0%, transparent 65%);
  pointer-events: none;
  animation: fairmont-glow-pulse 3s ease-in-out infinite alternate;
}

@keyframes fairmont-glow-pulse {
  from { opacity: 0.5; }
  to   { opacity: 1.0; }
}

.fairmont-pulse-badge {
  position: static;
  animation: none;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

@keyframes badge-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(180,122,22,0.6); }
  50%       { box-shadow: 0 0 0 6px rgba(180,122,22,0); }
}

/* ─── Train Track animation ────────────────────────────── */
.train-track-line {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.6) 30%, rgba(56,189,248,0.9) 50%, rgba(56,189,248,0.6) 70%, transparent 100%);
  background-size: 200% 100%;
  animation: train-track-move 2.2s linear infinite;
}

@keyframes train-track-move {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.train-arrow {
  display: inline-block;
  animation: train-arrow-slide 1.4s ease-in-out infinite;
}

@keyframes train-arrow-slide {
  0%, 100% { transform: translateX(0); opacity: 1; }
  50%      { transform: translateX(5px); opacity: 0.6; }
}

.hero-left-premium {
  position: relative;
}

.hero-article--fairmont::after {
  position: absolute;
  z-index: 12;
  inset: 0;
  content: '';
  border: 1px solid rgba(246, 211, 133, 0.26);
  pointer-events: none;
}

.hero-overlay--fairmont {
  background:
    linear-gradient(90deg, rgba(3, 19, 22, 0.92) 0%, rgba(4, 27, 29, 0.78) 33%, rgba(4, 23, 29, 0.28) 67%, rgba(2, 15, 19, 0.46) 100%),
    linear-gradient(0deg, rgba(2, 14, 17, 0.75) 0%, transparent 46%);
}

.hero-content-grid--fairmont {
  position: relative;
}

.hero-copy-premium {
  isolation: isolate;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.17);
  border-radius: 1.5rem;
  padding: 3.7rem 1.5rem 1.5rem;
  background:
    linear-gradient(140deg, rgba(3, 24, 27, 0.84), rgba(8, 42, 42, 0.6)),
    rgba(4, 27, 29, 0.6);
  box-shadow: 0 28px 60px -38px rgba(0, 0, 0, 0.94), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.hero-copy-premium::after {
  position: absolute;
  z-index: -1;
  top: -8rem;
  right: -7rem;
  width: 16rem;
  height: 16rem;
  border-radius: 9999px;
  content: '';
  background: radial-gradient(circle, rgba(212, 167, 77, 0.18) 0%, transparent 69%);
  pointer-events: none;
}

.fairmont-side-premium {
  position: relative;
  padding: 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 1.5rem;
  background: linear-gradient(145deg, rgba(3, 22, 25, 0.7), rgba(8, 40, 41, 0.54));
  box-shadow: 0 28px 58px -38px rgba(0, 0, 0, 0.9), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(16px);
}

.fairmont-side-title {
  text-shadow: 0 6px 16px rgba(0, 0, 0, 0.45);
}

.fairmont-hero-card-premium {
  background: linear-gradient(140deg, rgba(18, 49, 49, 0.82) 0%, rgba(60, 46, 18, 0.76) 100%);
  box-shadow: 0 18px 36px -26px rgba(0, 0, 0, 0.78), 0 0 0 1px rgba(212, 167, 77, 0.16);
}

.fairmont-hero-card-premium::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(120deg, transparent 15%, rgba(255, 255, 255, 0.12) 45%, transparent 72%);
  mix-blend-mode: screen;
  opacity: 0.35;
}

.fairmont-info-card {
  background: linear-gradient(135deg, rgba(8, 38, 40, 0.78), rgba(34, 45, 31, 0.62));
  border-color: rgba(212, 167, 77, 0.25);
  box-shadow: 0 14px 30px -24px rgba(0, 0, 0, 0.75), inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.fairmont-train-card .train-track-line {
  background: linear-gradient(90deg, transparent 0%, rgba(212, 165, 82, 0.6) 30%, rgba(212, 165, 82, 0.95) 50%, rgba(212, 165, 82, 0.6) 70%, transparent 100%);
}

.fairmont-guide-card .rounded-md {
  border-color: rgba(180, 122, 22, 0.28);
  background: rgba(180, 122, 22, 0.08);
}

.hero-badge-premium {
  top: 1.25rem !important;
  left: 1.25rem !important;
  right: auto !important;
  margin: 0 !important;
  animation: none;
  border: 1px solid rgba(249, 221, 158, 0.4);
  background: linear-gradient(135deg, rgba(177, 121, 27, 0.98), rgba(211, 163, 71, 0.9)) !important;
  box-shadow: 0 12px 26px -16px rgba(0, 0, 0, 0.75), inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.hero-title2-premium,
.hero-title-main-premium,
.hero-title3-premium,
.hero-desc-premium,
.hero-cta-wrap-premium {
  opacity: 0;
  transform: translateY(10px);
  animation: premium-text-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.hero-title2-premium {
  color: rgba(255, 248, 230, 0.95);
  letter-spacing: 0.035em;
  text-transform: uppercase;
  text-shadow: 0 10px 24px rgba(0, 0, 0, 0.5);
  font-size: clamp(0.8rem, 1.05vw, 1rem);
  font-weight: 750;
}

.hero-title-main-premium { 
  font-size: clamp(1.6rem, 4vw, 3.4rem);
  line-height: 1.02;
  text-shadow: 0 16px 34px rgba(0, 0, 0, 0.58), 0 0 20px color-mix(in srgb, var(--accent-400, #c39034) 22%, transparent);
  animation-delay: 0.1s;
}

.hero-title3-premium {
  display: inline-flex;
  align-items: center;
  height: auto !important;
  min-height: 2rem;
  margin-top: 0.55rem;
  padding: 0.35rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.075);
  color: rgba(240, 253, 250, 0.94);
  font-size: clamp(0.78rem, 1.05vw, 0.94rem);
  font-weight: 650;
  letter-spacing: 0.015em;
  text-shadow: 0 12px 28px rgba(0, 0, 0, 0.52);
  animation-delay: 0.18s;
}

/* Title3 icon circle */
.title3-icon-circle {
  box-shadow: 0 2px 10px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.22);
  border: 1px solid rgba(255,255,255,0.18);
}

/* Title3 rotate transition — soft & professional */
.title3-rotate-enter-active {
  transition: opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.55s cubic-bezier(0.22, 1, 0.36, 1),
              filter 0.55s cubic-bezier(0.22, 1, 0.36, 1);
}
.title3-rotate-leave-active {
  transition: opacity 0.4s cubic-bezier(0.4, 0, 1, 1),
              transform 0.4s cubic-bezier(0.4, 0, 1, 1),
              filter 0.4s cubic-bezier(0.4, 0, 1, 1);
  position: absolute;
  width: 100%;
}
.title3-rotate-enter-from {
  opacity: 0;
  transform: translateY(8px) scale(0.97);
  filter: blur(4px);
}
.title3-rotate-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0px);
}
.title3-rotate-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0px);
}
.title3-rotate-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(1.01);
  filter: blur(3px);
}

.hero-desc-premium {
  max-width: 34rem;
  color: rgba(240, 253, 250, 0.88);
  line-height: 1.65;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  animation-delay: 0.25s;
}

.hero-cta-wrap-premium {
  animation-delay: 0.33s;
}

@keyframes premium-text-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
    filter: blur(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

@keyframes premium-badge-float {
  0%,
  100% {
    transform: translateY(-40px);
  }
  50% {
    transform: translateY(-30px);
  }
}

.hero-shell {
  width: 100%;
  max-width: 1380px;
  margin-left: auto;
  margin-right: auto;
  border-radius: 0;
}

@media (min-width: 768px) {
  .hero-content-grid--fairmont {
    grid-template-columns: minmax(0, 1fr) minmax(320px, 380px);
  }

  .hero-copy-premium {
    padding: 4rem 2rem 2rem;
  }
}

.hero-article {
  min-height: 380px;
}

.hero-inner {
  min-height: 380px;
}



@media (min-width: 768px) {
  .hero-shell {
    border-radius: 1.5rem;
  }

  .hero-article,
  .hero-inner {
    /* Keep hero proportional but capped on large screens */
    min-height: clamp(500px, calc(100dvh - 170px), 680px);
    height: clamp(500px, calc(100dvh - 170px), 680px);
  }
 
}
.hero-carousel{
  padding: 0;
}

.hero-navigation {
  position: absolute;
  z-index: 30;
  top: 50%;
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  transform: translateY(-50%);
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 9999px;
  color: #fff;
  background: rgba(28, 25, 23, 0.48);
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.hero-navigation:hover,
.hero-navigation:focus-visible {
  background: rgba(28, 25, 23, 0.75);
}

.hero-navigation:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.hero-navigation-prev {
  left: 0.35rem;
}

.hero-navigation-next {
  right: 0.35rem;
}

.hero-pagination {
  position: absolute;
  z-index: 30;
  right: 0;
  bottom: 0.75rem;
  left: 0;
  display: flex;
  justify-content: center;
  gap: 0.4rem;
}

.hero-pagination-dot {
  position: relative;
  display: inline-flex;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: transparent;
}

.hero-pagination-dot::after {
  width: 0.5rem;
  height: 0.5rem;
  content: '';
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.42);
  transition: width 0.2s ease, background-color 0.2s ease;
}

.hero-pagination-dot.is-active::after {
  width: 1.5rem;
  background: #fff;
}

.hero-image-kabe {
  object-position: center 67%;
}

.hero-image-fairmont {
  object-position: center 68%;
}

@media (min-width: 768px) {
  .hero-image-kabe {
    object-position: center 67%;
  }

  .hero-image-fairmont {
    object-position: center 64%;
  }
}

.hero-pattern {
  background-image: radial-gradient(circle at 10% 20%, rgba(180, 122, 22, 0.2), transparent 30%),
    radial-gradient(circle at 85% 80%, rgba(15, 118, 110, 0.2), transparent 30%);
}

.fairmont-spotlight {
  position: absolute;
  inset: 0;
  z-index: 11;
  pointer-events: none;
}

.fairmont-spotlight-dot {
  position: absolute;
  left: 50%;
  top: 21%;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: #f0c66b;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.55), 0 0 18px rgba(240, 198, 107, 0.85);
}

.fairmont-spotlight-ring {
  position: absolute;
  left: calc(50% - 9px);
  top: calc(21% - 9px);
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  border: 1px solid rgba(240, 198, 107, 0.7);
  animation: fairmont-spotlight-pulse 1.8s ease-out infinite;
}

.fairmont-spotlight-line {
  position: absolute;
  left: calc(50% + 8px);
  top: calc(21% + 5px);
  width: 220px;
  height: 2px;
  background: linear-gradient(-90deg, rgba(240, 198, 107, 0.9) 0%, rgba(240, 198, 107, 0.2) 100%);
  transform: rotate(4deg);
  transform-origin: left center;
}

 

.fairmont-spotlight-tag {
  position: absolute;
  left: calc(50% + 200px);
  top: calc(36% - 92px);
  color: #fef3c7;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 9999px;
  border: 1px solid rgba(240, 198, 107, 0.5);
  background: rgba(15, 23, 42, 0.7);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}

@keyframes fairmont-spotlight-pulse {
  0% {
    opacity: 0.75;
    transform: scale(0.85);
  }
  100% {
    opacity: 0;
    transform: scale(1.5);
  }
}

@media (max-width: 1280px) {
  .fairmont-spotlight-line {
    width: 170px;
  }

  .fairmont-spotlight-arrow {
    left: calc(54% + 167px);
    top: calc(61% - 41px);
  }

  .fairmont-spotlight-tag {
    left: calc(56% + 86px);
    top: calc(33% - 76px);
    font-size: 9px;
  }
   .hero-article .container{
    padding-left:2rem!important;
  } 
}

.hero-cta-primary,
.hero-cta-secondary {
  display: inline-flex;
  gap: 0.55rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.9rem;
  min-height: 2.7rem;
  padding: 0.55rem 1rem;
  font-size: 0.84rem;
  font-weight: 750;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease, border-color 0.22s ease, background-color 0.22s ease;
}

.hero-cta-primary {
  color: #fff;
  border: 1px solid rgba(167, 243, 208, 0.24);
  background: linear-gradient(135deg, #0f766e, #0a5e59);
  box-shadow: 0 14px 28px -16px rgba(5, 81, 78, 0.95), inset 0 1px 0 rgba(255, 255, 255, 0.17);
}

.hero-cta-primary:hover {
  transform: translateY(-2px);
  border-color: rgba(209, 250, 229, 0.46);
  background: linear-gradient(135deg, #129084, #0f766e);
  box-shadow: 0 18px 34px -17px rgba(5, 81, 78, 1), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.hero-cta-secondary {
  color: rgba(248, 250, 252, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(3, 24, 27, 0.52);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.hero-cta-secondary:hover {
  transform: translateY(-2px);
  border-color: rgba(246, 211, 133, 0.6);
  background: rgba(71, 52, 15, 0.55);
  color: #fff;
}

.hero-cta-primary:focus-visible,
.hero-cta-secondary:focus-visible {
  outline: 2px solid #f6d385;
  outline-offset: 3px;
}

.hero-nav-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.58);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.hero-nav-btn:hover {
  background: rgba(180, 122, 22, 0.9);
  border-color: rgba(180, 122, 22, 0.9);
}

@media (max-width: 767px) {
  .hero-shell {
    border-width: 0;
    box-shadow: 0 22px 48px -30px rgba(15, 23, 42, 0.72);
  }

  .hero-article,
  .hero-inner {
    min-height: 400px;
    height: 400px;
  }

  .hero-article .hero-inner {
    padding: 1rem 2.25rem 3.2rem 3.5rem !important;
  }

  .hero-overlay {
    background:
      linear-gradient(180deg, rgba(4, 11, 21, 0.24) 0%, rgba(4, 11, 21, 0.08) 28%, rgba(4, 11, 21, 0.9) 78%, #040b15 100%),
      linear-gradient(90deg, rgba(4, 11, 21, 0.72), rgba(4, 11, 21, 0.08) 72%);
  }

  .hero-image-fairmont {
    object-position: 58% center;
  }

  .hero-meta {
    align-items: center;
  }

  .hero-license {
    border-color: rgba(255, 255, 255, 0.22);
    background: rgba(4, 11, 21, 0.5);
    padding: 0.35rem 0.65rem;
    font-size: 0.63rem;
    box-shadow: 0 8px 20px -14px rgba(0, 0, 0, 0.9);
  }

  .hero-content-grid {
    gap: 0;
    padding-bottom: 0;
  }

  .hero-left-premium {
    display: flex;
    max-width: 21rem;
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-copy-premium {
    overflow: visible;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
  }

  .hero-copy-premium::after {
    display: none;
  }

  .hero-badge-premium {
    position: static !important;
    order: -1;
    margin: 0 0 0.55rem !important;
    padding: 0.35rem 0.7rem;
    background: rgba(147, 98, 18, 0.92);
    font-size: 0.62rem;
    letter-spacing: 0.025em;
  }

  .hero-title2-premium {
    margin: 0 0 0.05rem;
    font-size: 1.28rem;
    line-height: 1.15;
  }

  .hero-title-script,
  .hero-title-main-premium.hero-title-script {
    margin: 0.08rem 0 0.35rem;
    font-family: 'Allura', cursive;
    font-size: 2.8rem;
    font-weight: 400;
    line-height: 0.95;
    letter-spacing: 0.01em;
    text-shadow: 0 12px 28px rgba(0, 0, 0, 0.58), 0 0 16px rgba(230, 185, 74, 0.22);
  }

  .hero-title2-premium,
  .hero-title-main-premium,
  .hero-title3-premium,
  .hero-desc-premium,
  .hero-cta-wrap-premium,
  .hero-badge-premium {
    animation: none;
    opacity: 1;
    transform: none;
    filter: none;
  }

  .hero-title-main-premium:not(.hero-title-script) {
    margin-bottom: 0.25rem;
    font-size: 2rem;
    line-height: 1.05;
  }

  .hero-title3-premium {
    width: 100%;
    min-height: 0;
    height: auto !important;
    margin-top: 0.45rem;
    padding: 0.28rem 0.6rem;
    font-size: 0.72rem;
    line-height: 1.3;
  }

  .hero-title3-premium img,
  .hero-title3-premium .title3-icon-circle {
    width: 1.6rem;
    height: 1.6rem;
  }

  .hero-desc-premium {
    display: -webkit-box;
    margin: 0.5rem 0 0;
    max-width: 20rem;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    color: rgba(248, 250, 252, 0.9);
    font-size: 0.78rem;
    line-height: 1.45;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
  }

  .hero-cta-wrap-premium {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
    margin-top: 0.85rem !important;
  }

  .hero-cta-primary,
  .hero-cta-secondary {
    min-height: 2.65rem;
    padding: 0.5rem 0.65rem;
    border-radius: 0.8rem;
    font-size: 0.72rem;
    line-height: 1.15;
  }

  .hero-cta-primary {
    box-shadow: 0 12px 24px -14px rgba(38, 164, 116, 0.95);
  }

  .hero-navigation {
    top: 31%;
    width: 2.5rem;
    height: 2.5rem;
    border-color: rgba(255, 255, 255, 0.18);
    background: rgba(4, 11, 21, 0.42);
    backdrop-filter: blur(8px);
  }

  .hero-navigation-prev { left: 0.65rem; }
  .hero-navigation-next { right: 0.65rem; }

  .hero-pagination {
    bottom: 0.95rem;
    gap: 0;
  }

  .hero-pagination-dot {
    width: 2.75rem;
    height: 2.75rem;
  }

  .hero-pagination-dot.is-active::after {
    width: 1.35rem;
  }
}
</style>
